import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, IndianRupee, MapPin, FilePlus2, RotateCcw, AlertCircle, RefreshCw } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, errMsg, fmtINR, fmtDate } from "../lib/api";

// Phase 142 — Daily Field Activity drill-down (Admin MIS Reports only).
// Collector row  ->  ward-wise rows  ->  mohalla-wise rows  ->  every visit / payment / survey / reassessment.
// Levels 1+2 come from one `/reports/field-activity/breakup` call per collector (cached per applied filters);
// level 3 lazily loads `/reports/field-activity/records` per mohalla bucket.

export const NONE_KEY = "none";

const RECORD_TYPES = {
  visit: { label: "Visit", icon: MapPin, cls: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] border-[hsl(var(--info))]/20" },
  payment: { label: "Payment", icon: IndianRupee, cls: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20" },
  survey: { label: "New survey", icon: FilePlus2, cls: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20" },
  reassessment: { label: "Reassessment", icon: RotateCcw, cls: "bg-primary/10 text-primary border-primary/20" },
};

export const RecordTypeBadge = ({ type, testId }) => {
  const t = RECORD_TYPES[type] || { label: type, icon: MapPin, cls: "" };
  const Icon = t.icon;
  return (
    <Badge variant="outline" className={`gap-1 font-medium text-[11px] whitespace-nowrap ${t.cls}`} data-testid={testId} data-record-type={type}>
      <Icon className="h-3 w-3" aria-hidden="true" /> {t.label}
    </Badge>
  );
};

/** Expansion state + per-filter caches for the whole table. `resetKey` (the applied filters key) clears everything.
 *  Phase 185: an optional `scope` = { id, params } narrows one collector's breakup to a supervisor section — `id` keys the
 *  cache (the same collector may sit in two sections), `params` (e.g. supervisor_id) ride along on the API calls. */
export function useFieldActivityDrilldown(code, params, resetKey) {
  const [open, setOpen] = useState(() => new Set());
  const [breakups, setBreakups] = useState({});   // cacheId (collectorId | scope.id) -> {loading, error, data}
  const [records, setRecords] = useState({});     // bucketKey  -> {loading, error, data}
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => { setOpen(new Set()); setBreakups({}); setRecords({}); }, [resetKey]);

  const isOpen = useCallback((key) => open.has(key), [open]);
  const toggle = useCallback((key) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  }), []);

  const loadBreakup = useCallback((collectorId, force = false, scope = null) => {
    const id = scope?.id || collectorId;
    setBreakups((prev) => {
      if (!force && prev[id] && (prev[id].loading || prev[id].data)) return prev;
      return { ...prev, [id]: { loading: true, error: null, data: null } };
    });
    api.get(`/t/${code}/reports/field-activity/breakup`, { params: { collector_id: collectorId, ...paramsRef.current, ...(scope?.params || {}) } })
      .then((r) => setBreakups((prev) => ({ ...prev, [id]: { loading: false, error: null, data: r.data } })))
      .catch((e) => setBreakups((prev) => ({ ...prev, [id]: { loading: false, error: errMsg(e), data: null } })));
  }, [code]);

  const loadRecords = useCallback((collectorId, wardKey, mohallaKey, force = false, scope = null) => {
    const key = `${scope?.id || collectorId}|${wardKey}|${mohallaKey}`;
    setRecords((prev) => {
      if (!force && prev[key] && (prev[key].loading || prev[key].data)) return prev;
      return { ...prev, [key]: { loading: true, error: null, data: null } };
    });
    api.get(`/t/${code}/reports/field-activity/records`, {
      params: { collector_id: collectorId, in_ward_id: wardKey, in_mohalla_id: mohallaKey, ...paramsRef.current, ...(scope?.params || {}) },
    })
      .then((r) => setRecords((prev) => ({ ...prev, [key]: { loading: false, error: null, data: r.data } })))
      .catch((e) => setRecords((prev) => ({ ...prev, [key]: { loading: false, error: errMsg(e), data: null } })));
  }, [code]);

  return useMemo(() => ({ isOpen, toggle, breakups, records, loadBreakup, loadRecords }),
    [isOpen, toggle, breakups, records, loadBreakup, loadRecords]);
}

const MetricCells = ({ row, prefix, strong = false }) => (
  <>
    <TableCell className="text-right tabular-nums text-sm" data-testid={`${prefix}-visits`}>{row.visits}</TableCell>
    <TableCell className="text-right tabular-nums text-sm" data-testid={`${prefix}-receipts`}>{row.collections_count}</TableCell>
    <TableCell className={`text-right tabular-nums text-sm ${strong ? "font-medium" : ""}`} data-testid={`${prefix}-amount`}>{fmtINR(row.collections_amount)}</TableCell>
    <TableCell className="text-right tabular-nums text-sm" data-testid={`${prefix}-surveys`}>{row.new_surveys}</TableCell>
    <TableCell className="text-right tabular-nums text-sm" data-testid={`${prefix}-reassess`}>{row.reassessments}</TableCell>
  </>
);

const Chevron = ({ open }) => (
  <ChevronRight className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150 ${open ? "rotate-90" : ""}`} aria-hidden="true" />
);

const toggleBtn = "inline-flex items-center gap-1.5 rounded-md -ml-1 pl-1 pr-2 py-1 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** Full-width helper rows (loading / error / empty) that keep the table columns aligned. */
const NoteRow = ({ colSpan, className = "", children, testId }) => (
  <TableRow className={`hover:bg-transparent ${className}`} data-testid={testId}>
    <TableCell colSpan={colSpan} className="py-2">{children}</TableCell>
  </TableRow>
);

const LoadingRows = ({ colSpan, indent, testId }) => (
  <NoteRow colSpan={colSpan} testId={testId}>
    <div className={`space-y-2 ${indent}`} aria-busy="true">
      <Skeleton className="h-4 w-2/5" />
      <Skeleton className="h-4 w-1/3" />
    </div>
  </NoteRow>
);

const ErrorRows = ({ colSpan, indent, message, onRetry, testId }) => (
  <NoteRow colSpan={colSpan} testId={testId}>
    <div className={`flex flex-wrap items-center gap-2 text-sm text-destructive ${indent}`} role="alert">
      <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{message || "Could not load this breakup."}</span>
      <Button type="button" variant="outline" size="sm" className="h-7" onClick={onRetry} data-testid={`${testId}-retry`}>
        <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
      </Button>
    </div>
  </NoteRow>
);

/** Level 3: the records of one mohalla bucket, as a nested table inside a single full-width row. */
export const RecordsRow = ({ state, colSpan, onRetry, testId }) => {
  const indent = "pl-20";
  if (!state || state.loading) return <LoadingRows colSpan={colSpan} indent={indent} testId={`${testId}-loading`} />;
  if (state.error) return <ErrorRows colSpan={colSpan} indent={indent} message={state.error} onRetry={onRetry} testId={`${testId}-error`} />;
  const d = state.data || {};
  const recs = d.records || [];
  return (
    <TableRow className="hover:bg-transparent bg-background" data-testid={testId}>
      <TableCell colSpan={colSpan} className="py-3 pl-20 pr-4">
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 border-b bg-muted/40 text-xs text-muted-foreground" data-testid={`${testId}-summary`}>
            <span className="font-medium text-foreground">{d.total ?? recs.length} record{(d.total ?? recs.length) === 1 ? "" : "s"}</span>
            {d.counts && (
              <>
                <span>{d.counts.visit} visit{d.counts.visit === 1 ? "" : "s"}</span>
                <span>· {d.counts.payment} payment{d.counts.payment === 1 ? "" : "s"} ({fmtINR(d.amount || 0)})</span>
                {d.counts.survey > 0 && <span>· {d.counts.survey} new survey{d.counts.survey === 1 ? "" : "s"}</span>}
                {d.counts.reassessment > 0 && <span>· {d.counts.reassessment} reassessment{d.counts.reassessment === 1 ? "" : "s"}</span>}
              </>
            )}
            {d.truncated && <span className="text-[hsl(var(--warning))]" data-testid={`${testId}-truncated`}>Showing the latest {recs.length} of {d.total}</span>}
            <span className="ml-auto">Newest first</span>
          </div>
          {recs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid={`${testId}-empty`}>No records in this mohalla for the selected period.</p>
          ) : (
            <div className="max-h-[420px] overflow-y-auto">
              <Table className="text-sm" data-testid={`${testId}-table`}>
                <TableHeader className="sticky top-0 bg-background/95 backdrop-blur">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 w-[130px]">Type</TableHead>
                    <TableHead className="h-9 w-[150px]">Date &amp; time</TableHead>
                    <TableHead className="h-9">PIN</TableHead>
                    <TableHead className="h-9">Owner</TableHead>
                    <TableHead className="h-9 text-right">Amount</TableHead>
                    <TableHead className="h-9">Outcome / detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recs.map((r, i) => (
                    <TableRow key={r.id || i} className="hover:bg-muted/60" data-testid={`${testId}-record-${i}`} data-record-type={r.type}>
                      <TableCell className="py-2"><RecordTypeBadge type={r.type} /></TableCell>
                      <TableCell className="py-2 whitespace-nowrap tabular-nums">
                        {fmtDate(r.date)}{r.time ? <span className="text-muted-foreground"> {r.time}</span> : null}
                      </TableCell>
                      <TableCell className="py-2 font-mono text-xs">{r.pin || "—"}</TableCell>
                      <TableCell className="py-2">{r.owner_name || "—"}</TableCell>
                      <TableCell className="py-2 text-right tabular-nums font-medium">{r.type === "payment" ? fmtINR(r.amount || 0) : <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="py-2 text-muted-foreground">
                        <span className="text-foreground">{r.outcome || "—"}</span>
                        {r.detail ? <span className="block text-xs truncate max-w-[360px]" title={r.detail}>{r.detail}</span> : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

/** Levels 1-3 for one expanded collector: ward rows, their mohalla rows and the records of any open mohalla.
 *  Phase 185: `scopeKey` / `scopeParams` (a supervisor section) limit the breakup to that supervisor's wards and keep the
 *  cache / open-state / test ids apart when the same collector appears in two sections. */
export const CollectorBreakupRows = ({ drill, collectorId, colSpan, testIdPrefix, scopeKey = "", scopeParams = null }) => {
  const { isOpen, toggle, breakups, records, loadBreakup, loadRecords } = drill;
  const cid = scopeKey ? `${scopeKey}|${collectorId}` : collectorId;          // cache + open-state id
  const tid = scopeKey ? `${scopeKey.replace(/[^a-zA-Z0-9_-]+/g, "-")}-${collectorId}` : collectorId;   // test-id safe
  const scope = useMemo(() => (scopeKey ? { id: cid, params: scopeParams || {} } : null), [scopeKey, cid, scopeParams]);
  const state = breakups[cid];
  useEffect(() => { loadBreakup(collectorId, false, scope); }, [collectorId, loadBreakup, scope]);

  if (!state || state.loading) return <LoadingRows colSpan={colSpan} indent="pl-9" testId={`${testIdPrefix}-breakup-loading-${tid}`} />;
  if (state.error) {
    return <ErrorRows colSpan={colSpan} indent="pl-9" message={state.error} onRetry={() => loadBreakup(collectorId, true, scope)}
      testId={`${testIdPrefix}-breakup-error-${tid}`} />;
  }
  const wards = state.data?.wards || [];
  if (wards.length === 0) {
    return (
      <NoteRow colSpan={colSpan} testId={`${testIdPrefix}-breakup-empty-${tid}`}>
        <p className="pl-9 text-sm text-muted-foreground">No ward-wise activity for this collector in the selected period.</p>
      </NoteRow>
    );
  }
  return (
    <>
      {wards.map((w) => {
        const wKey = `w|${cid}|${w.ward_id}`;
        const wOpen = isOpen(wKey);
        const wId = `${testIdPrefix}-ward-row-${tid}-${w.ward_id}`;
        return (
          <React.Fragment key={w.ward_id}>
            <TableRow className="bg-muted/20 hover:bg-muted/50" data-testid={wId} data-level="ward" data-open={wOpen ? "true" : "false"}>
              <TableCell className="py-2 pl-9">
                <button type="button" className={toggleBtn} onClick={() => toggle(wKey)} aria-expanded={wOpen}
                  title={wOpen ? "Hide mohalla-wise breakup" : "Show mohalla-wise breakup"}
                  data-testid={`${testIdPrefix}-expand-ward-${tid}-${w.ward_id}`}>
                  <Chevron open={wOpen} />
                  <span className="text-sm font-medium">{w.ward_name}</span>
                  {w.zone_name && <span className="text-xs text-muted-foreground">· {w.zone_name}</span>}
                  <span className="text-[11px] text-muted-foreground">({w.mohallas.length} mohalla{w.mohallas.length === 1 ? "" : "s"})</span>
                </button>
              </TableCell>
              <MetricCells row={w} prefix={`${testIdPrefix}-ward-cell-${tid}-${w.ward_id}`} strong />
            </TableRow>
            {wOpen && w.mohallas.map((m) => {
              const mKey = `m|${cid}|${w.ward_id}|${m.mohalla_id}`;
              const mOpen = isOpen(mKey);
              const mId = `${testIdPrefix}-mohalla-row-${tid}-${w.ward_id}-${m.mohalla_id}`;
              const recKey = `${cid}|${w.ward_id}|${m.mohalla_id}`;
              return (
                <React.Fragment key={m.mohalla_id}>
                  <TableRow className="bg-muted/40 hover:bg-muted/60" data-testid={mId} data-level="mohalla" data-open={mOpen ? "true" : "false"}>
                    <TableCell className="py-2 pl-16">
                      <button type="button" className={toggleBtn} aria-expanded={mOpen}
                        onClick={() => { toggle(mKey); if (!mOpen) loadRecords(collectorId, w.ward_id, m.mohalla_id, false, scope); }}
                        title={mOpen ? "Hide records" : "Show every visit and payment in this mohalla"}
                        data-testid={`${testIdPrefix}-expand-mohalla-${tid}-${w.ward_id}-${m.mohalla_id}`}>
                        <Chevron open={mOpen} />
                        <span className={`text-sm ${m.mohalla_id === NONE_KEY ? "italic text-muted-foreground" : ""}`}>{m.mohalla_name}</span>
                      </button>
                    </TableCell>
                    <MetricCells row={m} prefix={`${testIdPrefix}-mohalla-cell-${tid}-${w.ward_id}-${m.mohalla_id}`} />
                  </TableRow>
                  {mOpen && (
                    <RecordsRow state={records[recKey]} colSpan={colSpan}
                      onRetry={() => loadRecords(collectorId, w.ward_id, m.mohalla_id, true, scope)}
                      testId={`${testIdPrefix}-records-${tid}-${w.ward_id}-${m.mohalla_id}`} />
                  )}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};
